import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const css = `
  .carousel-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding: 20px;
    position: relative;
    z-index: 1;
  }
  .carousel-title {
    font-family: var(--font-display);
    font-size: 1.4rem;
    font-weight: 700;
    color: #D4AF37;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    text-shadow: 0 0 30px rgba(212,175,55,0.4);
    text-align: center;
  }
  .carousel-hint {
    font-family: var(--font-body);
    font-size: 0.85rem;
    color: rgba(200,200,220,0.5);
    text-align: center;
  }

  /* Table + ring container */
  .carousel-ring-area {
    position: relative;
    width: 340px;
    height: 340px;
  }

  /* Round table */
  .carousel-table {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 65%;
    height: 65%;
    border-radius: 50%;
    background: radial-gradient(ellipse at 40% 35%, rgba(60,40,20,0.9), rgba(30,18,8,0.95) 70%);
    border: 3px solid rgba(212,175,55,0.25);
    box-shadow: 0 0 40px rgba(212,175,55,0.08), inset 0 0 25px rgba(0,0,0,0.5);
  }
  .carousel-table::after {
    content: '';
    position: absolute;
    inset: 12%;
    border-radius: 50%;
    border: 1px solid rgba(212,175,55,0.08);
  }

  /* Each box slot on the ring */
  .carousel-slot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 52px;
    height: 64px;
    margin-left: -26px;
    margin-top: -32px;
    cursor: pointer;
    transition: transform 0.3s ease, filter 0.3s ease;
  }
  .carousel-slot:hover {
    transform: var(--slot-transform) scale(1.18) !important;
    filter: brightness(1.25);
    z-index: 50 !important;
  }

  /* Box styling */
  .cbox-lid {
    position: absolute;
    top: 0; left: -1px; right: -1px; height: 28%;
    background: linear-gradient(180deg, #42A5F5, #1976D2);
    border-radius: 4px 4px 1px 1px;
    border: 1px solid rgba(66,165,245,0.4);
    box-shadow: 0 2px 6px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15);
    z-index: 2;
  }
  .cbox-body {
    position: absolute;
    bottom: 0; left: 0; right: 0; height: 78%;
    background: linear-gradient(180deg, #2196F3 0%, #1976D2 30%, #1565C0 60%, #0D47A1 100%);
    border-radius: 3px 3px 5px 5px;
    border: 1px solid rgba(33,150,243,0.4);
    box-shadow: 0 5px 15px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    overflow: hidden;
  }
  .cbox-number {
    background: linear-gradient(135deg, #C8962A, #F0D060 40%, #D4AF37 70%, #C8962A);
    color: #2a1800;
    font-family: var(--font-display);
    font-size: 0.8rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 3px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3);
    z-index: 3;
    min-width: 22px;
    text-align: center;
  }
  .cbox-region {
    font-size: 0.38rem;
    font-family: var(--font-display);
    font-weight: 600;
    color: rgba(255,255,255,0.8);
    text-transform: uppercase;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    padding: 0 3px;
    z-index: 3;
  }
  .cbox-selected .cbox-body {
    border-color: #D4AF37;
    box-shadow: 0 0 20px rgba(212,175,55,0.5), 0 5px 15px rgba(0,0,0,0.4);
  }

  @media (max-width: 500px) {
    .carousel-ring-area { width: 320px; height: 320px; }
    .carousel-slot { width: 36px; height: 44px; margin-left: -18px; margin-top: -22px; }
    .cbox-number { font-size: 0.6rem; padding: 1px 5px; }
    .cbox-region { display: none; }
    .carousel-title { font-size: 1.1rem; }
    .carousel-table { width: 55% !important; height: 55% !important; }
  }
  @media (max-width: 380px) {
    .carousel-ring-area { width: 290px; height: 290px; }
    .carousel-slot { width: 32px; height: 40px; margin-left: -16px; margin-top: -20px; }
    .cbox-number { font-size: 0.55rem; padding: 1px 4px; }
    .carousel-table { width: 50% !important; height: 50% !important; }
  }
`;

export default function BoxCarousel({ boxes = [], onBoxClick, disabled = false }) {
  const sorted = [...boxes].sort((a, b) => a.box_number - b.box_number);
  const count = sorted.length;
  const [ready, setReady] = useState(false);

  // Brief delay before showing boxes (entrance animation)
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 300);
    return () => clearTimeout(t);
  }, []);

  // Calculate ring radius as % of container
  const ringRadius = 43; // percent from center

  return (
    <div className="carousel-wrap">
      <style>{css}</style>

      <motion.div
        className="carousel-title"
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        Scegli la tua scatola
      </motion.div>

      <div className="carousel-hint">
        Tocca una scatola per sceglierla
      </div>

      <div className="carousel-ring-area">
        {/* Round table surface */}
        <div className="carousel-table" />

        {/* Boxes around the ring */}
        {sorted.map((box, i) => {
          const angle = (i / count) * 360 - 90; // start from top
          const rad = (angle * Math.PI) / 180;
          const x = 50 + Math.cos(rad) * ringRadius;
          const y = 50 + Math.sin(rad) * ringRadius;
          const transform = `translate(${(x - 50) / 50 * 26}px, ${(y - 50) / 50 * 26}px)`;

          return (
            <motion.div
              key={box.box_number}
              className="carousel-slot"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                '--slot-transform': transform,
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={ready ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.1 + i * 0.04, type: 'spring', stiffness: 300, damping: 20 }}
              onClick={() => !disabled && onBoxClick?.(box.box_number)}
            >
              <div className="cbox-lid" />
              <div className="cbox-body">
                <div className="cbox-number">{box.box_number}</div>
                {box.region_name && (
                  <div className="cbox-region">{box.region_name}</div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
